import { useEffect, useState } from 'react';
import { operatorConsole, type SecurityState } from '../../lib/api/operatorConsole';
import { IpWhitelistCard } from './IpWhitelistCard';
import { TeamTwoFactorCard } from './TeamTwoFactorCard';
import { TwoFactorCard } from './TwoFactorCard';

export function SecurityPage() {
  const [state, setState] = useState<SecurityState | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    void operatorConsole
      .getSecurity()
      .then((s) => {
        if (alive) setState(s);
      })
      .catch((err: unknown) => {
        if (alive) setLoadError(err instanceof Error ? err.message : 'Failed to load security settings');
      });
    return () => {
      alive = false;
    };
  }, []);

  if (loadError) {
    return (
      <main className="p-6">
        <p className="text-[var(--co-error)]" role="alert">
          {loadError}
        </p>
      </main>
    );
  }
  if (!state) {
    return <main className="p-6 text-[var(--co-muted)]">Loading…</main>;
  }

  return (
    <main className="p-6 flex flex-col gap-6 max-w-2xl">
      <h1 className="text-xl font-semibold">Security</h1>

      {/* Yours first — this is the part everyone on the team has. The
       *  fallbacks matter during a deploy: a backend that predates personal
       *  settings sends no `personal` block, and reading through it blank
       *  would throw during render, which react-router answers by replacing
       *  the whole shell with an error page rather than just this tab. */}
      <IpWhitelistCard
        scope="personal"
        currentIp={state.current_ip}
        whitelist={state.personal?.console_ip_whitelist ?? []}
        onSaved={setState}
      />
      <TwoFactorCard
        enrolled={state.personal?.two_factor_enrolled ?? false}
        onEnabled={() => {
          // We know what just happened — 2FA is on for this person — so the
          // card can move on even if the refetch fails. Without this the
          // "I saved my recovery codes" button would sit dead on a network
          // blip, and the codes are gone the moment the page reloads.
          void operatorConsole
            .getSecurity()
            .then(setState)
            .catch(() =>
              setState((current) =>
                current
                  ? {
                      ...current,
                      personal: { ...current.personal, two_factor_enrolled: true },
                    }
                  : current,
              ),
            );
        }}
      />

      {/* The perimeter for the whole console. Owner only — the backend serves
       *  this block to nobody else. */}
      {state.operator && (
        <>
          <h2 className="mt-2 text-[13px] font-semibold uppercase tracking-wide text-[var(--co-muted)]">
            Whole console
          </h2>
          <IpWhitelistCard
            scope="operator"
            currentIp={state.current_ip}
            whitelist={state.operator.console_ip_whitelist}
            onSaved={setState}
          />
          <TeamTwoFactorCard
            required={state.operator.require_team_2fa}
            selfEnrolled={state.personal?.two_factor_enrolled ?? false}
            onChanged={setState}
          />
        </>
      )}
    </main>
  );
}
